import { useMutation } from '@apollo/client';
import { renameExperimentVariantMutation } from 'api/mutations/experiments';
import { updateExperimentEditVariants } from 'modules/experiments/takeover/ducks/actions';
import { MutateVariant, VariantInfo } from 'modules/experiments/types';
import { addNegativeToast, addPositiveToast, errorIdentifier } from 'modules/toastContainer';
import { useDispatch } from 'react-redux';
import { useExperimentsTranslations } from '../useTranslations';

const source = 'useRenameExperimentVariant';

export const useRenameExperimentVariant = ({ experimentId }: MutateVariant) => {
  const { t } = useExperimentsTranslations();
  const dispatch = useDispatch();
  const errorMessage = `Failed to rename variant for experiment id ${experimentId}`;
  const errorToasterMessage = t('errorMessage.renameVariant');

  const errorHandler = (error: unknown, additional = {}) => {
    dispatch(
      addNegativeToast(
        errorToasterMessage,
        errorIdentifier({
          errorMessage,
          source,
          error,
          additional,
        }),
      ),
    );
  };

  const [renameExperimentVariant] = useMutation(renameExperimentVariantMutation, {
    onError: (e) => errorHandler(e),
  });

  return async ({ variantId, variantName }: VariantInfo, locale: string): Promise<boolean> => {
    const { data } = await renameExperimentVariant({
      variables: {
        experimentId,
        variationId: variantId,
        variationName: variantName,
        ...(locale && { locale }),
      },
    });

    if (data?.renameVariation?.variations) {
      const successToasterMessage = t('successMessage.renameVariant');
      dispatch(addPositiveToast(successToasterMessage));

      // We need to save the original BE variant object to the redux state
      // We have selectors which will mutate this date when a component needs it
      dispatch(updateExperimentEditVariants(data.renameVariation.variations));
      return true;
    }

    if (data?.renameVariation?.message) {
      errorHandler(data.renameVariation.message, { variantId, newName: variantName });
    }
    return false;
  };
};
